
import { unzipSync, strFromU8, type Unzipped } from "fflate";
import { resolveThemeChain } from "./ooxmlPaths";
import { COMMON_FONTS, NS_A } from "../../../const";
import { getActivePresentationBuffer } from "../../../utils/officePptx";
import { hasPpt, ppt } from "../../../utils/formatUtils";

export interface FontOption {
  name: string;
  /** "Title" | "Body" | "PPT" | "" — shown as a datalist hint. */
  tag: string;
}

export interface FontData {
  themeFonts: { majorFont: string | null; minorFont: string | null };
  usedFonts: string[];
  allFonts: FontOption[];
}

let _cached: FontData | null = null;

/** Load (and cache) the combined font list for the active presentation. */
export async function loadFontData(): Promise<FontData> {
  if (_cached) return _cached;
  try {
    const buf = await getActivePresentationBuffer();
    const zip = unzipSync(new Uint8Array(buf));

    const themeFonts = extractThemeFonts(zip);
    const usedFonts = extractUsedFonts(zip);
    _cached = { themeFonts, usedFonts, allFonts: buildFontList(themeFonts, usedFonts) };
    return _cached;
  } catch {
    // Non-critical: fall back to the common font list if the deck can't be read.
    _cached = {
      themeFonts: { majorFont: null, minorFont: null },
      usedFonts: [],
      allFonts: COMMON_FONTS.map((name) => ({ name, tag: "" })),
    };
    return _cached;
  }
}

/** Clear the cached font list (call when the deck may have changed). */
export function clearFontDataCache(): void {
  _cached = null;
}

/** The font of the first selected shape, or null if none/untyped. */
export async function detectSelectedFont(): Promise<string | null> {
  if (!hasPpt()) return null; // not in PowerPoint — no selection to read (expected, not an error)
  let detected: string | null = null;
  try {
    await ppt().run(async (context) => {
      const sel = context.presentation.getSelectedShapes();
      sel.load("items");
      await context.sync();
      const first = sel.items?.[0];
      if (!first) return;
      const font = first.textFrame.textRange.font;
      font.load("name");
      await context.sync();
      detected = font.name || null;
    });
  } catch {
    /* shape may have no text frame (image, etc.) */
  }
  return detected;
}

/* ── OOXML parsing ──────────────────────────────────────────────── */

/** Theme major/minor Latin typefaces from the active slide's theme. */
function extractThemeFonts(zip: Unzipped): { majorFont: string | null; minorFont: string | null } {
  const themePath = resolveActiveThemePath(zip) ?? "ppt/theme/theme1.xml";
  const file = zip[themePath];
  if (!file) return { majorFont: null, minorFont: null };

  const doc = new DOMParser().parseFromString(strFromU8(file), "text/xml");
  const scheme = doc.getElementsByTagNameNS(NS_A, "fontScheme")[0];
  if (!scheme) return { majorFont: null, minorFont: null };

  const latinOf = (tag: string): string | null => {
    const el = scheme.getElementsByTagNameNS(NS_A, tag)[0];
    const latin = el?.getElementsByTagNameNS(NS_A, "latin")[0];
    return latin?.getAttribute("typeface") || null;
  };
  return { majorFont: latinOf("majorFont"), minorFont: latinOf("minorFont") };
}

/** Every Latin/complex-script/east-asian typeface used across the deck. */
function extractUsedFonts(zip: Unzipped): string[] {
  const fonts = new Set<string>();
  const re = /<a:(?:latin|cs|ea)\b[^>]*\btypeface="([^"]+)"/g;
  for (const path of Object.keys(zip)) {
    if (!/^ppt\/(slides|slideMasters|slideLayouts)\/[^/]+\.xml$/.test(path)) continue;
    const xml = strFromU8(zip[path]!);
    for (let m = re.exec(xml); m; m = re.exec(xml)) {
      const tf = m[1]!;
      if (tf && !tf.startsWith("+")) fonts.add(tf);
    }
  }
  return [...fonts].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

/**
 * Theme first (tagged), then deck-used fonts, then the common fallback —
 * deduped case-insensitively so each font appears once.
 */
function buildFontList(
  themeFonts: { majorFont: string | null; minorFont: string | null },
  usedFonts: string[],
): FontOption[] {
  const out: FontOption[] = [];
  const seen = new Set<string>();
  const add = (name: string | null, tag: string): void => {
    if (!name) return;
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ name, tag });
  };

  add(themeFonts.majorFont, "Title");
  add(themeFonts.minorFont, "Body");
  for (const f of usedFonts) add(f, "PPT");
  for (const f of COMMON_FONTS) add(f, "");
  return out;
}

/* ── Theme-path resolution (slide → layout → master → theme) ────── */

/** The active deck's theme path (slide 1's chain), or null when unresolved. */
function resolveActiveThemePath(zip: Unzipped): string | null {
  const slides = Object.keys(zip)
    .filter((p) => /^ppt\/slides\/slide\d+\.xml$/.test(p))
    .sort((a, b) => Number.parseInt(/slide(\d+)/.exec(a)![1]!, 10) - Number.parseInt(/slide(\d+)/.exec(b)![1]!, 10));
  const slideFile = slides[0]?.split("/").pop();
  if (!slideFile) return null; // deck has no slides — expected, not an error
  return resolveThemeChain(zip, slideFile).themePath;
}
